import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import supportChatService, {
  type SupportThread,
} from '@/services/supportChatService'
import { SupportConversationPanel } from '@/components/support/SupportConversationPanel'
import { useSupportChatEvents } from '@/hooks/useSupportChatEvents'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useShopStorefrontLocale } from '@/contexts/ShopStorefrontLocaleContext'
import { buildStorefrontCustomerLoginHref } from '@/lib/storefrontCheckoutLogin'

/**
 * Floating help widget on shop storefronts for logged-in web customers.
 */
export function SupportChatWidget({
  enabled = true,
  shopSlug,
}: {
  enabled?: boolean
  /** Storefront slug — required for guest “Need help?” to open that shop’s login. */
  shopSlug?: string
}) {
  const { isAuthenticated, user } = useAuthStore()
  const { t } = useShopStorefrontLocale()
  const { slug: paramSlug } = useParams<{ slug: string }>()
  const location = useLocation()
  const shopId = user?.shop_id
  const isCustomer = Boolean(isAuthenticated && user?.is_web_user && shopId)

  const [open, setOpen] = useState(false)
  const [threads, setThreads] = useState<SupportThread[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [composing, setComposing] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [unread, setUnread] = useState(0)
  const [messageRefreshNonce, setMessageRefreshNonce] = useState(0)

  const selected = useMemo(
    () => threads.find((thread) => thread.thread_id === selectedId) || null,
    [threads, selectedId],
  )

  const loginHref = useMemo(() => {
    const slug = (shopSlug || paramSlug || '').trim()
    if (!slug) return '/auth/customer/login'
    const returnPath = `${location.pathname}${location.search || ''}`
    return buildStorefrontCustomerLoginHref({
      slug,
      returnPath: returnPath.includes(slug) ? returnPath : `/shop/${slug}`,
    })
  }, [shopSlug, paramSlug, location.pathname, location.search])

  const load = useCallback(async () => {
    if (!enabled || !shopId || !isCustomer) return
    setLoading(true)
    try {
      const [listRes, unreadRes] = await Promise.all([
        supportChatService.listThreads({ shop_id: shopId, status: 'all', limit: 20 }),
        supportChatService.getUnread(shopId),
      ])
      if (listRes.success && listRes.data) {
        setThreads(listRes.data.threads)
        setSelectedId((prev) => prev ?? listRes.data.threads[0]?.thread_id ?? null)
      }
      if (unreadRes.success && unreadRes.data) {
        setUnread(unreadRes.data.unread_threads)
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false)
    }
  }, [enabled, shopId, isCustomer])

  useEffect(() => {
    if (!enabled || !isCustomer) return
    void load()
  }, [enabled, isCustomer, load])

  useSupportChatEvents({
    shopId,
    enabled: enabled && isCustomer && open,
    onEvent: () => {
      void load()
      setMessageRefreshNonce((n) => n + 1)
    },
  })

  if (!enabled) return null

  if (!isCustomer) {
    // Guests: compact help that links to login
    return (
      <div className="fixed bottom-5 right-5 z-40">
        <Link
          to={loginHref}
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-slate-800"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          {t('support.needHelp')}
        </Link>
      </div>
    )
  }

  const startThread = async () => {
    if (!body.trim()) return
    setSending(true)
    try {
      const res = await supportChatService.createThread({
        subject: subject.trim() || undefined,
        body: body.trim(),
      })
      if (res.success && res.data) {
        setComposing(false)
        setSubject('')
        setBody('')
        await load()
        setSelectedId(res.data.thread.thread_id)
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3">
      {open && (
        <div className="flex h-[min(560px,70vh)] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <header className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
            <div>
              <div className="text-sm font-semibold">{t('support.title')}</div>
              <div className="text-xs text-slate-300">{t('support.hoursHint')}</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 hover:bg-white/10"
              aria-label={t('support.close')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </header>

          {composing ? (
            <div className="flex flex-1 flex-col gap-3 p-4">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('support.subjectPlaceholder')}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                placeholder={t('support.bodyPlaceholder')}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setComposing(false)}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm"
                >
                  {t('support.back')}
                </button>
                <button
                  type="button"
                  disabled={sending || !body.trim()}
                  onClick={() => void startThread()}
                  className="flex-1 rounded-lg bg-slate-900 py-2 text-sm text-white disabled:opacity-50"
                >
                  {sending ? t('support.sending') : t('support.send')}
                </button>
              </div>
            </div>
          ) : selected ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  {t('support.allChats')}
                </button>
                <button
                  type="button"
                  onClick={() => setComposing(true)}
                  className="text-xs font-medium text-slate-900 hover:underline"
                >
                  {t('support.new')}
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <SupportConversationPanel
                  thread={selected}
                  mode="customer"
                  enableEventStream={false}
                  refreshNonce={messageRefreshNonce}
                  onThreadUpdated={(updated) => {
                    setThreads((prev) =>
                      prev.map((thread) =>
                        thread.thread_id === updated.thread_id ? { ...thread, ...updated } : thread,
                      ),
                    )
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex justify-end border-b border-gray-100 px-3 py-2">
                <button
                  type="button"
                  onClick={() => setComposing(true)}
                  className="text-xs font-medium text-slate-900 hover:underline"
                >
                  {t('support.newConversation')}
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-10">
                    <LoadingSpinner />
                  </div>
                ) : threads.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-gray-500">
                    {t('support.noConversations')}
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setComposing(true)}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white"
                      >
                        {t('support.askQuestion')}
                      </button>
                    </div>
                  </div>
                ) : (
                  threads.map((thread) => (
                    <button
                      key={thread.thread_id}
                      type="button"
                      onClick={() => setSelectedId(thread.thread_id)}
                      className="w-full border-b border-gray-50 px-3 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="truncate text-sm font-medium text-gray-900">
                        {thread.subject || thread.last_message_preview || t('support.conversation')}
                      </div>
                      <div className="text-[11px] capitalize text-gray-400">{thread.status}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v)
          if (!open) void load()
        }}
        className="relative inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-slate-800"
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5" />
        {open ? t('support.close') : t('support.needHelp')}
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold">
            {unread}
          </span>
        )}
      </button>
    </div>
  )
}
